import { Segment } from '@dna/analytics'
import { RespondToViewport, WithProfiler } from '@dna/mixins'
import { LitElement, css, html, nothing, when } from '@lit'

let v2DrawerExists = false

function initV2Drawer() {
  if (v2DrawerExists) return
  v2DrawerExists = true

  document
    .querySelector('.js-desktop-nav-v2-overlay')
    ?.addEventListener('mouseenter', closeV2Drawer)
  document.addEventListener('dna:header:close', closeV2Drawer)
}

function openV2Drawer(key) {
  initV2Drawer()

  const $links = document.querySelector('.desktop-nav__links')

  $links?.setAttribute('data-v2-open', 'true')

  for (const $nav of document.querySelectorAll('dna-desktop-nav[has-menu]')) {
    $nav.setAttribute('v2-drawer-open', '')
  }

  for (const $nav of document.querySelectorAll('dna-desktop-nav')) {
    const isActive = $nav.getAttribute('key') === key

    $nav.active = isActive
    $nav.setAttribute('aria-expanded', isActive ? 'true' : 'false')

    if (isActive) {
      $nav._updateChildrenAccess(0)
    }
  }
  document.body.style.overflow = 'hidden'
}

function closeV2Drawer() {
  const $links = document.querySelector('.desktop-nav__links')

  if (!$links?.hasAttribute('data-v2-open')) return

  $links.removeAttribute('data-v2-open')
  document.body.style.overflow = 'scroll'

  for (const $nav of document.querySelectorAll('dna-desktop-nav[has-menu]')) {
    $nav.setAttribute('v2-drawer-closing', '')
    $nav.removeAttribute('v2-drawer-open')
  }

  setTimeout(() => {
    for (const $nav of document.querySelectorAll('dna-desktop-nav[has-menu]')) {
      $nav.removeAttribute('v2-drawer-closing')
    }
  }, 400)

  for (const $nav of document.querySelectorAll('dna-desktop-nav')) {
    $nav.active = false
    $nav.setAttribute('aria-expanded', 'false')
    $nav._deactivateV2Nav?.()
  }
}

document.addEventListener('dna:tech-1638-v2', initV2Drawer)

class DNADesktopNav extends WithProfiler(RespondToViewport(LitElement)) {
  static properties = {
    key: { type: Number },
    active: { type: Boolean, reflect: true },
    href: { type: String },
    navTitle: { type: String },
    hasMenu: { type: Boolean, attribute: 'has-menu' },
    hovered: { type: Boolean },
    tech1638V2: { type: Boolean },
  }

  constructor() {
    super()

    this._setMobileBreakpoints(['mobile', 'tablet'])
    this.hovered = false
    this.tech1638V2 = false
  }

  connectedCallback() {
    super.connectedCallback()

    this.l1text = this.getAttribute('navTitle')
    document.addEventListener('dna:tech-1638-v2', this._handleTECH1638V2)
    if (this.tech1638V2) {
      this._handleTECH1638V2()
    }
    document.addEventListener('dna:header:close', this._handleOverlayMouseover)
    this.addEventListener('focusin', this._handleItemFocus)
    this.addEventListener('focusin', this._handleLinkMouseover)
    this.addEventListener('mouseenter', this._handleLinkMouseover)
    this.addEventListener('click', this._menuItemClickHandler)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    document.removeEventListener('dna:tech-1638-v2', this._handleTECH1638V2)
    document.removeEventListener(
      'dna:header:close',
      this._handleOverlayMouseover,
    )
    this.removeEventListener('focusin', this._handleItemFocus)
    this.removeEventListener('focus', this._handleLinkMouseover)
    this.removeEventListener('mouseover', this._handleLinkMouseover)
    this.removeEventListener('click', this._menuItemClickHandler)
  }

  _handleTECH1638V2 = () => {
    if (this.tech1638V2) {
      return
    }
    this.tech1638V2 = true
    this.active = false
    initV2Drawer()
    closeV2Drawer()
  }

  _updateChildrenAccess = (index = 1) => {
    // TODO: refactor this to avoid query selector (currently needed to find slotted a tags)
    const $childLinks = this.querySelectorAll('a')
    for (let i = 0; i < $childLinks.length; i++) {
      const $link = $childLinks[i]
      $link.setAttribute('tabindex', index)
    }
  }

  _handleItemFocus = (event) => {
    const item = event.target.closest('a')
    if (item && this.contains(event.target) && !this.tech1638V2) {
      this.active = true
    }
  }

  _deactivateV2Nav = () => {
    if (
      document.activeElement.classList.contains('js-main-menu-section') ||
      this.contains(document.activeElement)
    ) {
      return
    }
    this._updateChildrenAccess(-1)
  }

  _handleLinkMouseover = (event) => {
    document.dispatchEvent(new CustomEvent('dna:country-selector:hide'))
    document.dispatchEvent(new CustomEvent('dna:search-bar:close'))

    if (this.tech1638V2) {
      if (this.hasMenu) {
        openV2Drawer(this.getAttribute('key'))
        if (!this.hovered) {
          this._trackMegaMenuOpen()
          this.hovered = true
        }
      } else {
        closeV2Drawer()
      }
      return
    }

    this.dispatchEvent(
      new CustomEvent('dna:header:close', { bubbles: true, composed: true }),
    )
    this.active = true
    const isTECH1638 = document
      .querySelector('.js-header-wrapper')
      ?.classList.contains('apply-tech-1638')
    if (isTECH1638) {
      document.body.style.overflow = 'hidden'
    }

    this._updateChildrenAccess(0)

    if (this.hasMenu) {
      this.setAttribute('aria-expanded', true)
      if (!this.hovered) {
        this._trackMegaMenuOpen()
        this.hovered = true
      }
    }
  }

  _handleOverlayMouseover = (event) => {
    if (this.tech1638V2) {
      return
    }
    this.active = false
    const isTECH1638 = document
      .querySelector('.js-header-wrapper')
      ?.classList.contains('apply-tech-1638')
    if (isTECH1638) {
      document.body.style.overflow = ''
    }

    this.setAttribute('aria-expanded', false)
    if (
      document.activeElement.classList.contains('js-main-menu-section') ||
      this.contains(document.activeElement)
    ) {
      return
    }
    this._updateChildrenAccess(-1)

    if (
      event.type !== 'dna:header:close' &&
      event?.target?.classList.contains('overlay')
    ) {
      this.hovered = false
    }
  }

  _menuItemClickHandler(event) {
    const $clickedLink = event.target.closest('a')

    // L1
    if (event.target.closest('.js-main-menu-section-item')) {
      const $menuSection = event.target.closest('.js-main-menu-section')
      const $menuSectionLink =
        $menuSection.renderRoot.querySelector('.js-menu-item')
      if ($menuSectionLink) {
        this.l1link = $menuSectionLink.getAttribute('href')?.trim()
        this.l2link = false
        this.l2text = false
        this.l3link = false
        this.l3text = false
        this._trackMenuItemClick()
      }
    }
    // L2
    if (
      event.target.closest('.js-solo-menu-item') ||
      event.target.closest('.js-featured-menu-item')
    ) {
      this.l2link = $clickedLink?.getAttribute('href')?.trim()
      this.l2text = $clickedLink?.textContent?.trim()
      this.l3link = false
      this.l3text = event.target.closest('.js-featured-menu-item')
        ? 'Image'
        : false
      this._trackMenuItemClick()
    }
    // L2 & L3
    if (event.target.closest('.js-main-menu-child')) {
      const $menuChild = event.target.closest('.js-main-menu-child')
      this.l2text = $menuChild
        .querySelector('.js-main-menu-child-title')
        ?.textContent?.trim()

      if (event.target.closest('.js-main-menu-child-title')) {
        this.l2link = $clickedLink?.getAttribute('href')?.trim()
        this.l3link = false
        this.l3text = false
        this._trackMenuItemClick()
      }
      if (event.target.closest('.js-main-menu-child-item')) {
        this.l3link = $clickedLink?.getAttribute('href')?.trim()
        this.l3text = $clickedLink?.textContent?.trim()
        this._trackMenuItemClick()
      }
    } else {
      if (event.target.closest('.js-main-menu-child-item')) {
        this.l2link = $clickedLink?.getAttribute('href')?.trim()
        this.l2text = $clickedLink?.textContent?.trim()
        this.l3link = false
        this.l3text = false
        this._trackMenuItemClick()
      }
    }
  }

  _trackMegaMenuOpen() {
    // @segment
    Segment.protect(() => {
      Segment.track(Segment.CONSTANTS.EVENTS.MAIN_MENU_SECTION_EXPANDED, {
        main_menu_section: this.navTitle,
      })
    })
  }

  _trackMenuItemClick() {
    // @segment
    Segment.protect(() => {
      const menuItemLink =
        !this.l3link && !this.l2link
          ? this.l1link
          : !this.l3link
            ? this.l2link
            : this.l3link
      const menuItemText =
        !this.l3text && !this.l2text
          ? this.l1text
          : !this.l3text
            ? this.l2text
            : this.l3text
      let menuItemTreeString
      if (this.l1text) {
        menuItemTreeString = this.l1text
      }
      if (this.l2text) {
        if (this.l1text) {
          menuItemTreeString = `${this.l1text} - ${this.l2text}`
        } else {
          menuItemTreeString = this.l2text
        }
      }
      if (this.l3text) {
        if (this.l1text && this.l2text) {
          menuItemTreeString = `${this.l1text} - ${this.l2text} - ${this.l3text}`
        } else if (this.l2text) {
          menuItemTreeString = `${this.l2text} - ${this.l3text}`
        } else {
          menuItemTreeString = this.l3text
        }
      }

      Segment.track(Segment.CONSTANTS.EVENTS.MAIN_MENU_ITEM_CLICKED, {
        main_menu_item: menuItemTreeString,
        main_menu_item_link: menuItemLink,
        main_menu_item_text: menuItemText,
      })
    })
  }

  render() {
    return html`
      ${when(
        this.isMobile,
        () => nothing,
        () => html`
          <li>
            ${when(
              this.href === '#',
              () => html`
                <div tabindex="0">
                  <slot name="link"></slot>
                </div>
              `,
              () => html`
                <a href=${this.href} class="js-menu-item">
                  <slot name="link"></slot>
                </a>
              `,
            )}
            ${when(
              !this.tech1638V2,
              () => html`
                <div class="menu">
                  <slot name="menu"></slot>
                </div>
                <div
                  @mouseover=${this._handleOverlayMouseover}
                  class="overlay"
                ></div>
              `,
            )}
            ${when(
              this.tech1638V2,
              () => html`
                <div class="menu menu--side-drawer">
                  <slot name="menu"></slot>
                </div>
              `,
            )}
          </li>
        `,
      )}
    `
  }

  static styles = css`
    :host([active]) .overlay {
      visibility: visible;
      opacity: 1;
    }
    :host([active]) .menu:not(.menu--side-drawer) {
      max-height: 1000px;
      transition: max-height 1s ease;
      transition-delay: .4s;
    }
    :host([v2-drawer-open]) .menu.menu--side-drawer,
    :host([v2-drawer-closing]) .menu.menu--side-drawer {
      height: calc(100dvh - var(--dna-header-height));
      max-height: none;
      overflow-x: hidden;
      overflow-y: auto;
      opacity: 1;
      background: var(--dna-brand-reverse);
    }
     :host([v2-drawer-open]) .menu.menu--side-drawer {
      width: 670px;
      pointer-events: auto;
    }
    :host([v2-drawer-closing]) .menu.menu--side-drawer {
      width: 0;
      pointer-events: none;
    }
    :host([v2-drawer-open][active]) .menu.menu--side-drawer {
      z-index: calc(var(--dna-z-index-sticky, 600) + 2);
    }
    :host([v2-drawer-open]:not([active])) .menu.menu--side-drawer {
      pointer-events: none;
      z-index: calc(var(--dna-z-index-sticky, 600) + 1);
    }
    @media screen and (min-width: 1440px) {
      :host([v2-drawer-open]) .menu.menu--side-drawer {
        width: 48vw;
      }
    }
    .menu.menu--side-drawer {
      position: fixed;
      left: 0;
      top: var(--dna-header-height);
      width: 0;
      max-height: none;
      opacity: 1;
      pointer-events: none;
      z-index: calc(var(--dna-z-index-sticky, 600) + 1);
      overflow-x: hidden;
      overflow-y: auto;
      box-sizing: border-box;
      transition: width 0.4s ease;
    }
    .menu.menu--side-drawer ::slotted([slot='menu']) {
      height: 100%;
    }
    li > a {
      color: inherit;
      cursor: pointer;
      text-decoration: none;
    }
    li > div {
      cursor: default;
    }
    :host(:not([has-menu])) .menu {
      display: none;
    }
    :host(:not([has-menu])) .overlay {
      display: none;
    }
    .menu:not(.menu--side-drawer) {
      position: absolute;
      width: 100%;
      left: 0;
      top: 100%;
      z-index: 1;
      overflow: hidden;
      height: auto;
      max-height: 0px;
    }
    .overlay {
      position: fixed;
      width: 100%;
      height: 100%;
      min-height: 100vh;
      top: var(--dna-header-height);
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(1, 0, 0, 0.5);
      z-index: -1;
      visibility: hidden;
      opacity: 0;
    }
  `
}

class DNADesktopNavChild extends WithProfiler(RespondToViewport(LitElement)) {
  static properties = {
    href: { type: String },
    current: { type: Boolean, reflect: true },
  }

  constructor() {
    super()
    this._setMobileBreakpoints(['mobile', 'tablet'])
  }

  render() {
    return html`
      ${when(
        this.isMobile,
        () => nothing,
        () => html`
          <div
            ?current=${this.current}
            class="wrapper"
          >
            ${when(
              this.href === null,
              () => html`
                <a
                  href=${this.href}
                  class="js-main-menu-child-title js-menu-item"
                >
                  <slot
                    name="title"
                    class="js-main-menu-title-slot"
                  ></slot>
                </a>
              `,
              () => html`
                <slot
                  name="title"
                  class="js-main-menu-title-slot js-menu-item"
                ></slot>
              `,
            )}
            <div class="inner-links">
              <slot></slot>
            </div>
          </div>
        `,
      )}
    `
  }

  static styles = css`
    .wrapper {
      page-break-inside: avoid;
      break-inside: avoid-column;
    }
    a {
      text-decoration: none;
      color: inherit;
    }

    .inner-links {
      margin-top: var(--dna-space-4);
      padding-left: var(--dna-space-4);
      border-left: .5px solid var(--dna-color-light-grey);
      margin-bottom: var(--dna-space-10);
    }
    :host(.apply-tech-1638-v2) .inner-links {
      border-left: none;
    }
    @media screen and (min-width: 1440px) {
      :host(.apply-tech-1638-v2) .inner-links {
        padding-left: 0;
      }
    }
    ::slotted(.main-menu-child-item-list) {
      display: flex;
      flex-direction: column;
      gap: calc(var(--dna-space-5) / 2);
      margin-top: calc(var(--dna-space-2) * -1)

    }
  `
}

customElements.define('dna-desktop-nav', DNADesktopNav)
customElements.define('dna-desktop-nav-child', DNADesktopNavChild)
